import { useEffect } from 'react';

import { findScrollTargets } from '../../utils';

import { calculateScrollDistance, getRandomInterval, isAtScrollEnd, smoothScrollTo } from './scrollMath';

export const useScrollAnimation = (
	scrollerRef: React.RefObject<HTMLDivElement | null>,
	intervalSeconds: number,
	shouldAnimate: boolean,
	setShouldAnimate: (animate: boolean) => void,
) => {
	useEffect(() => {
		const scroller = scrollerRef.current;
		if (!(scroller && shouldAnimate)) {
			return undefined;
		}

		let timeoutId: number | undefined;

		const handleInterval = () => {
			const { next, last } = findScrollTargets(scroller);

			if (isAtScrollEnd(scroller, last)) {
				setShouldAnimate(false);
				return;
			}

			if (!next) return;

			const scrollDistance = calculateScrollDistance(scroller);
			const targetScrollTop = scroller.scrollTop + scrollDistance;
			const maxScrollTop = scroller.scrollHeight - scroller.clientHeight;
			const clampedTargetScrollTop = Math.min(targetScrollTop, maxScrollTop);

			// If the clamped target is the same as current position, we're done
			if (Math.abs(clampedTargetScrollTop - scroller.scrollTop) < 1) {
				setShouldAnimate(false);
				return;
			}

			const animationDuration = 500 + Math.random() * 300; // 500-800ms
			smoothScrollTo(scroller, clampedTargetScrollTop, animationDuration);

			// Schedule next iteration with random timing
			const nextDelay = getRandomInterval(intervalSeconds);
			timeoutId = window.setTimeout(handleInterval, nextDelay);
		};

		// Start the first iteration with random timing
		const initialDelay = getRandomInterval(intervalSeconds);
		timeoutId = window.setTimeout(handleInterval, initialDelay);

		return () => {
			if (timeoutId !== undefined) {
				window.clearTimeout(timeoutId);
			}
		};
	}, [shouldAnimate, intervalSeconds, scrollerRef, setShouldAnimate]);
};
